import { useMemo } from "react";
import DateFilters from "../../components/DateFilters";
import ResumoMontagens from "../../components/ResumoMontagens";
import ResumoEntregas from "../../components/ResumoEntregas";
import DashboardTable from "../../components/DashboardTable";
import type { DashboardColumnKind } from "../../components/DashboardTable";
import { DashboardPrimeiraLinha } from "./components/DashboardPrimeiraLinha";
import { DashboardOrigemCharts } from "./components/DashboardOrigemCharts";
import { EntregaProvider } from "../../components/ResumoEntregas/EntregaContext";
import { DashboardProvider, useDashboard } from "./context/DashboardContext";
import { useMontagemUsuarios } from "../../hooks/useProducao";
import { useEntregaUsuarios } from "../../hooks/useEntrega";
import { Bike, Layers3 } from "lucide-react";

const resumoUsuarioColumnKinds: DashboardColumnKind[] = [
  "text",
  "text",
  "number",
  "duration",
];

const DashboardContent = () => {
  const {
    startDate,
    endDate,
    selectedLojaId,
    appliedStartDate,
    appliedEndDate,
    appliedLojaId,
    lojas,
    setStartDate,
    setEndDate,
    setSelectedLojaId,
    handleApplyFilter,
  } = useDashboard();

  const appliedSummary = useMemo(
    () => ({
      dataInicio: appliedStartDate,
      dataFim: appliedEndDate,
      lojaNome: appliedLojaId
        ? (lojas.find((l) => l.id === appliedLojaId)?.nome ?? null)
        : null,
    }),
    [appliedStartDate, appliedEndDate, appliedLojaId, lojas]
  );

  const { data: entregaUsuariosData } = useEntregaUsuarios({
    dataInicio: appliedStartDate,
    dataFim: appliedEndDate,
    lojaId: appliedLojaId || undefined,
  });

  const { data: montagemUsuariosData } = useMontagemUsuarios({
    dataInicio: appliedStartDate,
    dataFim: appliedEndDate,
    lojaId: appliedLojaId || undefined,
  });

  const entregaTableData =
    entregaUsuariosData?.usuarios.map((usuario) => [
      usuario.nome,
      usuario.roleDescricao,
      usuario.quantidadeEntregas.toString(),
      usuario.tempoMedio,
    ]) || [];

  const montagemTableData =
    montagemUsuariosData?.usuarios.map((usuario) => [
      usuario.nome,
      usuario.roleDescricao,
      usuario.quantidadeProducoes.toString(),
      usuario.tempoMedio,
    ]) || [];

  return (
    <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <DateFilters
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onApplyFilter={handleApplyFilter}
        lojas={lojas}
        selectedLojaId={selectedLojaId}
        onLojaChange={setSelectedLojaId}
        appliedSummary={appliedSummary}
      />

      <div className="space-y-10">
        <DashboardPrimeiraLinha
          dataInicio={appliedStartDate}
          dataFim={appliedEndDate}
          lojaId={appliedLojaId}
        />

        <ResumoMontagens
          dataInicio={appliedStartDate}
          dataFim={appliedEndDate}
          lojaId={appliedLojaId}
        />

        <EntregaProvider
          dataInicio={appliedStartDate}
          dataFim={appliedEndDate}
          lojaId={appliedLojaId}
        >
          <ResumoEntregas />
        </EntregaProvider>

        <DashboardOrigemCharts
          dataInicio={appliedStartDate}
          dataFim={appliedEndDate}
          lojaId={appliedLojaId}
        />

        <section
          className="space-y-3"
          aria-labelledby="dashboard-tabelas-usuarios"
        >
          <h2
            id="dashboard-tabelas-usuarios"
            className="text-lg font-semibold text-neutral-800"
          >
            Resumo por usuário
          </h2>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <DashboardTable
              title="Resumo de Montagens"
              icon={<Layers3 className="text-primary" size={20} />}
              headers={["Usuário", "Função", "Montagens", "Tempo Médio"]}
              data={montagemTableData}
              columnKinds={resumoUsuarioColumnKinds}
            />
            <DashboardTable
              title="Resumo de Entregas"
              icon={<Bike className="text-blue-600" size={20} />}
              headers={["Usuário", "Função", "Entregas", "Tempo Médio"]}
              data={entregaTableData}
              columnKinds={resumoUsuarioColumnKinds}
            />
          </div>
        </section>
      </div>
    </main>
  );
};

const Dashboard = () => {
  return (
    <DashboardProvider>
      <DashboardContent />
    </DashboardProvider>
  );
};

export default Dashboard;
